import { type ReactNode, useEffect, useRef, useState } from "react";

import { t } from "i18next";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Box, Tooltip } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { CSSObject } from "@mui/system";

import {
  filterCellSx,
  filterRowSx,
  headerCellBaseSx,
  headerRowSx,
  resizeHandleSx,
  sortIconSx
} from "@/src/components/organisms/AdminDataTable/adminDataTable.styles";
import ColumnFilterInput, {
  ColumnFilterMeta
} from "@/src/components/organisms/AdminDataTable/ColumnFilterInput";
import {
  Header,
  Table,
  flexRender
} from "@/src/components/organisms/AdminDataTable/reactTable";

interface AdminDataTableHeaderProps<TData extends object> {
  table: Table<TData>;
  hasAnyColumnFilter: boolean;
  filterResetVersion: number;
}

const getPinningStyles = <TData extends object>(
  header: Header<TData, unknown>,
  theme: Theme
): CSSObject => {
  const pinned = header.column.getIsPinned();
  if (!pinned) return {};
  if (pinned === "start") {
    return {
      position: "sticky",
      left: header.column.getStart("start"),
      top: 0,
      zIndex: 5,
      boxShadow: `1px 0 0 0 ${theme.palette.action.selected}`
    };
  }
  return {
    position: "sticky",
    right: header.column.getAfter("end"),
    top: 0,
    zIndex: 5,
    boxShadow: `-1px 0 0 0 ${theme.palette.action.selected}`
  };
};

const useTheadHeaderBlockHeight = (headerRowCount: number) => {
  const ref = useRef<HTMLTableSectionElement | null>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const thead = ref.current;
    if (!thead || headerRowCount === 0) return;

    const update = () => {
      const trs = thead.querySelectorAll<HTMLTableRowElement>(":scope > tr");
      let total = 0;
      for (let i = 0; i < headerRowCount; i++) {
        const tr = trs[i];
        if (tr) total += tr.getBoundingClientRect().height;
      }
      setHeight(total);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(thead);
    const trs = thead.querySelectorAll<HTMLTableRowElement>(":scope > tr");
    for (let i = 0; i < headerRowCount; i++) {
      const tr = trs[i];
      if (tr) observer.observe(tr);
    }

    return () => observer.disconnect();
  }, [headerRowCount]);

  return { ref, height };
};

interface SortIndicatorProps {
  dir: false | "asc" | "desc";
}

const SortIndicator = ({ dir }: SortIndicatorProps) => {
  if (dir === "asc") return <ArrowUpwardIcon sx={sortIconSx} />;
  if (dir === "desc") return <ArrowDownwardIcon sx={sortIconSx} />;
  return <UnfoldMoreIcon sx={{ ...sortIconSx, opacity: 0.4 }} />;
};

interface HeaderLabelProps {
  children: ReactNode;
  tooltipTitle?: string;
}

const HeaderLabel = (props: HeaderLabelProps) => {
  const { children, tooltipTitle } = props;

  const labelBox = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {children}
    </Box>
  );

  if (!tooltipTitle) return labelBox;

  return (
    <Tooltip title={tooltipTitle} placement="top-start" enterDelay={400}>
      {labelBox}
    </Tooltip>
  );
};

const AdminDataTableHeader = <TData extends object>(
  props: AdminDataTableHeaderProps<TData>
) => {
  const { table, hasAnyColumnFilter, filterResetVersion } = props;
  const theme = useTheme();
  const headerGroups = table.getHeaderGroups();
  const { ref: headerTheadRef, height: headerRowHeight } =
    useTheadHeaderBlockHeight(headerGroups.length);

  return (
    <Box component="thead" ref={headerTheadRef}>
      {headerGroups.map((headerGroup) => (
        <Box component="tr" key={headerGroup.id} sx={headerRowSx}>
          {headerGroup.headers.map((header) => {
            if (header.isPlaceholder) {
              return (
                <Box
                  component="th"
                  key={header.id}
                  scope="col"
                  colSpan={header.colSpan}
                  style={{ width: header.getSize() }}
                  sx={[headerCellBaseSx, getPinningStyles(header, theme)]}
                />
              );
            }

            const canSort = header.column.getCanSort();
            const sortDir = header.column.getIsSorted();
            let ariaSort: "ascending" | "descending" | "none" = "none";
            if (sortDir === "asc") {
              ariaSort = "ascending";
            } else if (sortDir === "desc") {
              ariaSort = "descending";
            }
            const rawHeader = header.column.columnDef.header;
            const headerMeta = header.column.columnDef.meta as
              | ColumnFilterMeta
              | undefined;
            const tooltipTitle =
              headerMeta?.headerTooltip ??
              (typeof rawHeader === "string" ? rawHeader : undefined);
            const headerLabelAndSort = (
              <>
                <HeaderLabel tooltipTitle={tooltipTitle}>
                  {flexRender(rawHeader, header.getContext())}
                </HeaderLabel>
                {canSort && <SortIndicator dir={sortDir} />}
              </>
            );
            const headerRowLayoutSx = {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            };

            return (
              <Box
                component="th"
                key={header.id}
                scope="col"
                colSpan={header.colSpan}
                aria-sort={canSort ? ariaSort : undefined}
                style={{ width: header.getSize() }}
                sx={[headerCellBaseSx, getPinningStyles(header, theme)]}
              >
                {canSort ? (
                  <Box
                    component="button"
                    type="button"
                    onClick={header.column.getToggleSortingHandler()}
                    sx={{
                      ...headerRowLayoutSx,
                      width: "100%",
                      margin: 0,
                      padding: 0,
                      border: "none",
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      font: "inherit",
                      color: "inherit",
                      textAlign: "inherit",
                      "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: theme.palette.semantic.primary.main,
                        outlineOffset: -2
                      }
                    }}
                  >
                    {headerLabelAndSort}
                  </Box>
                ) : (
                  <Box sx={headerRowLayoutSx}>{headerLabelAndSort}</Box>
                )}
                {header.column.getCanResize() && (
                  <Box
                    component="span"
                    role="separator"
                    aria-label={t(
                      "admin.dataTable.header.resizeColumnAriaLabel"
                    )}
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    onClick={(e) => e.stopPropagation()}
                    sx={resizeHandleSx}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      ))}
      {hasAnyColumnFilter && (
        <Box component="tr" sx={filterRowSx}>
          {table.getVisibleLeafColumns().map((column) => {
            const meta = column.columnDef.meta as ColumnFilterMeta | undefined;
            const showFilter =
              column.getCanFilter() && Boolean(meta?.filterVariant);
            const pinned = column.getIsPinned();
            let pinSx: CSSObject = {};
            if (pinned === "start") {
              pinSx = {
                left: column.getStart("start"),
                zIndex: 5,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `1px 0 0 0 ${theme.palette.action.selected}`
              };
            } else if (pinned) {
              pinSx = {
                right: column.getAfter("end"),
                zIndex: 5,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `-1px 0 0 0 ${theme.palette.action.selected}`
              };
            }
            return (
              <Box
                component="td"
                key={column.id}
                data-pinned={Boolean(pinned)}
                style={{ width: column.getSize() }}
                sx={[filterCellSx, pinSx, { top: headerRowHeight }]}
              >
                {showFilter && (
                  <ColumnFilterInput
                    key={`${column.id}-${filterResetVersion}`}
                    column={column}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default AdminDataTableHeader;
